import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { getTranslatedRoute } from "@/i18n/routeMap";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentLang, setLanguage, t } = useLanguage();

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setLangDropdownOpen(false);
  }, [location]);

  const handleLangChange = (lang: "en" | "es") => {
    if (lang === currentLang) return;
    const newPath = getTranslatedRoute(location.pathname, lang);
    setLanguage(lang);
    navigate(newPath);
    setLangDropdownOpen(false);
  };

  const serviceItems = [
    { labelKey: "home.servicesSliding", path: "/sliding-door-repair" },
    { labelKey: "home.servicesGlass", path: "/glass-replacement" },
    { labelKey: "home.servicesHardware", path: "/window-hardware-repair" },
    { labelKey: "home.servicesWeather", path: "/weatherstripping-sealing" },
    { labelKey: "home.servicesMaint", path: "/window-cleaning-maintenance" },
    { labelKey: "home.servicesBoardup", path: "/emergency-board-up" },
  ];

  return (
    <nav className="fixed top-8 left-0 right-0 z-50 bg-white shadow-sm transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-brand bg-primary">
              <i className="ri-shield-flash-line text-xl text-white" />
            </div>
            <div className="hidden sm:block">
              <div className="text-sm font-black leading-tight tracking-tight text-secondary">
                IMPACT WINDOW
              </div>
              <div className="text-xs font-bold leading-tight text-primary">
                REPAIR MIAMI
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-secondary hover:text-primary transition-colors"
            >
              {t("nav.home")}
            </Link>

            {/* Services Dropdown */}
            <div className="relative">
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center gap-1 text-sm font-medium text-secondary hover:text-primary transition-colors"
              >
                {t("nav.services")}
                <i
                  className={`ri-arrow-down-s-line transition-transform ${
                    servicesOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-brand shadow-lg border border-stone-100 py-2 z-50">
                  {serviceItems.map((s) => (
                    <Link
                      key={s.path}
                      to={s.path}
                      className="block px-4 py-2 text-sm text-secondary hover:bg-[#fefce8] hover:text-primary transition-colors"
                    >
                      {t(s.labelKey)}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/service-areas/"
              className={`text-sm font-medium text-secondary hover:text-primary transition-colors ${location.pathname === "/service-areas/" ? "!text-primary border-b-2 border-primary" : ""}`}
            >
              {t("nav.serviceAreas")}
            </Link>

            <Link
              to="/reviews/"
              className={`text-sm font-medium text-secondary hover:text-primary transition-colors ${location.pathname === "/reviews/" ? "!text-primary border-b-2 border-primary" : ""}`}
            >
              {t("nav.stories")}
            </Link>

            <Link
              to="/blog/"
              className={`text-sm font-medium text-secondary hover:text-primary transition-colors ${location.pathname.startsWith("/blog/") ? "!text-primary" : ""}`}
            >
              {t("nav.blog")}
            </Link>

            <Link
              to="/legal-disclaimer/"
              className="text-sm font-medium text-secondary hover:text-primary transition-colors"
            >
              {t("nav.contact")}
            </Link>
          </div>

          {/* CTA + Language Toggle + Mobile Toggle */}
          <div className="flex items-center gap-3">
            {/* Desktop Language Pill */}
            <div className="hidden md:flex items-center bg-[#fff8e7] border-2 border-primary rounded-full p-1">
              <button
                onClick={() => handleLangChange("en")}
                className={`px-3 py-1 text-xs font-bold rounded-full transition-colors whitespace-nowrap ${
                  currentLang === "en"
                    ? "bg-primary text-white"
                    : "text-gray-500 hover:text-primary"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => handleLangChange("es")}
                className={`px-3 py-1 text-xs font-bold rounded-full transition-colors whitespace-nowrap ${
                  currentLang === "es"
                    ? "bg-primary text-white"
                    : "text-gray-500 hover:text-primary"
                }`}
              >
                ES
              </button>
            </div>

            <a
              href="tel:+17863774452"
              className="hidden md:inline-flex items-center gap-2 bg-[#ea580c] text-white px-5 py-2.5 text-sm font-bold rounded-brand hover:bg-[#c2410c] transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-line" />
              (786) 377-4452
            </a>

            {/* Mobile Language Dropdown */}
            <div className="relative lg:hidden">
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="w-10 h-10 flex items-center justify-center rounded-brand text-secondary"
              >
                <i className="ri-global-line text-xl" />
              </button>
              {langDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-brand shadow-lg border border-stone-100 py-2 z-50 min-w-[140px]">
                  <button
                    onClick={() => handleLangChange("en")}
                    className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                      currentLang === "en"
                        ? "text-primary font-bold"
                        : "text-secondary hover:text-primary hover:bg-[#fefce8]"
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => handleLangChange("es")}
                    className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                      currentLang === "es"
                        ? "text-primary font-bold"
                        : "text-secondary hover:text-primary hover:bg-[#fefce8]"
                    }`}
                  >
                    Español
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-brand text-secondary"
            >
              <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-stone-100">
          <div className="px-4 py-4 space-y-1">
            <Link to="/" className="block px-3 py-2 text-sm font-medium text-secondary hover:text-primary hover:bg-[#fefce8] rounded-brand">
              {t("nav.home")}
            </Link>
            <div className="px-3 py-2 text-xs font-bold text-stone-400 uppercase tracking-wider">
              {t("nav.services")}
            </div>
            {serviceItems.map((s) => (
              <Link
                key={s.path}
                to={s.path}
                className="block px-6 py-2 text-sm text-secondary hover:text-primary hover:bg-[#fefce8] rounded-brand"
              >
                {t(s.labelKey)}
              </Link>
            ))}
            <Link to="/service-areas/" className={`block px-3 py-2 text-sm font-medium hover:text-primary hover:bg-[#fefce8] rounded-brand ${location.pathname === "/service-areas/" ? "text-primary bg-[#fefce8]" : "text-secondary"}`}>
              {t("nav.serviceAreas")}
            </Link>
            <Link to="/reviews/" className={`block px-3 py-2 text-sm font-medium hover:text-primary hover:bg-[#fefce8] rounded-brand ${location.pathname === "/reviews/" ? "text-primary bg-[#fefce8]" : "text-secondary"}`}>
              {t("nav.stories")}
            </Link>
            <Link to="/blog/" className={`block px-3 py-2 text-sm font-medium hover:text-primary hover:bg-[#fefce8] rounded-brand ${location.pathname.startsWith("/blog/") ? "text-primary bg-[#fefce8]" : "text-secondary"}`}>
              {t("nav.blog")}
            </Link>
            <Link to="/legal-disclaimer/" className="block px-3 py-2 text-sm font-medium text-secondary hover:text-primary hover:bg-[#fefce8] rounded-brand">
              {t("nav.contact")}
            </Link>
            <a
              href="tel:+17863774452"
              className="flex items-center justify-center gap-2 bg-[#ea580c] text-white px-5 py-3 text-sm font-bold rounded-brand mt-3"
            >
              <i className="ri-phone-line" />
              {t("nav.callNow")}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}